import { getUOMConversionString } from "c8-platform";
import Controller from "csi/action/Controller";
import locale from "csi/locale";
import reflection from "csi/reflection";
import siteDefaultLocale from "csi/siteDefaultLocale";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import declare from "dojo/_base/declare";

/**
 * MarkForDeletionUOMCustomAttribute uses the same logic as MarkForDeletionCustomAttribute.js but instead
 * applies the logic to both attributes in the UOM attribute duo to mark both attributes for deletion.
 */
const MarkForDeletionUOMCustomAttribute = declare(Controller, {

	bMarkForDeletion: true,

	/** @property {C8Node} _uomValueAttr - Value attribute node */
	_uomValueAttr: null,

	/** @property {C8Node} _conversionTypeField - Conversion attribute node */
	_uomConversionAttr: null,

	/** @property {String} uomNameResource - Localized resource for UnitOfMeasure.Name */
	uomNameResource: null,

	/** @property {String} caNameResource - Localized resource for ConfigurableAttribute.Plural */
	caNameResource: null,

	/**
     * @override
     * Find UOM Conversion custom attribute
     */
	onStart(): void {
		const node = this.getNode();
		this._uomValueAttr = node;
		this._uomConversionAttr = this.getCache().getNode(getUOMConversionString(node.$URL));
	},

	/**
     * @override
     * Add confirmation message
     */
	confirmAction(params): void {
		if (this.bMarkForDeletion) {
			params.message = locale.resource("MarkForDeletionUOMCustomAttribute_ConfirmationDialog", [this._uomValueAttr.$Name, this._uomConversionAttr.$Name]);
		}
		this.inherited(arguments);
	},

	/** 
	 * @override
	 * Check the current UOM attribute duo to see if they can be deleted
	 */
	preProcess(): void {
		// summary: Do not allow delete custom attribute if it is used in Custom Action
		if (this._nodeIsUsedInActions(this._uomValueAttr) || this._nodeIsUsedInActions(this._uomConversionAttr)) {
			this.displayErrorDialog({ suffix: "_ErrorUsedInActions" });
		} else {
			this.inherited(arguments);
		}
	},

	/** 
	 * @override
	 * Mark/unmark relevant UOM attributes for deletion and update UOMModule Delete List
	 */
	toWriter(): void {
		this.inherited(arguments);
		this.m_writer.changeNode(this._uomValueAttr.$URL).changeAttribute("MarkedForDeletion", xml.ATTR_BOOLEAN, this.bMarkForDeletion);
		this.m_writer.changeNode(this._uomConversionAttr.$URL).changeAttribute("MarkedForDeletion", xml.ATTR_BOOLEAN, this.bMarkForDeletion);
		
		// Call operation to update UOMModule Delete List
		if (this.bMarkForDeletion) {
			this.m_writer.callMethod("SiteAdmin", "AddUOMAttrKeyToUOMModuleDeleteList")
				.addParameter("AttributeName", xml.ATTR_STRING, this._uomValueAttr.$Name)
				.addParameter("ReflectionAttributeOwner", xml.ATTR_STRING, this._uomValueAttr.ReflectionAttributeOwner);
		} else {
			this.m_writer.callMethod("SiteAdmin", "RemoveUOMAttrKeyFromUOMModuleDeleteList")
				.addParameter("AttributeName", xml.ATTR_STRING, this._uomValueAttr.$Name)
				.addParameter("ReflectionAttributeOwner", xml.ATTR_STRING, this._uomValueAttr.ReflectionAttributeOwner);
		}
	},

	/** 
	 * @override
	 * Refresh the nodes after the UOM attribute duo is marked for deletion
	 */
	postProcessSuccess(): void {
		this.inherited(arguments);
		const view = this.widget;
		const uomValueAttrRow = view.getRefNodeByUrl(this._uomValueAttr.$URL);
		const uomConversionAttrRow = view.getRefNodeByUrl(this._uomConversionAttr.$URL);
		view.redrawNode(uomValueAttrRow);
		view.redrawNode(uomConversionAttrRow);
	},

	_nodeIsUsedInActions(nodeCA: C8Node): boolean {
		if (!nodeCA) {
			return false;
		}

		const attrId = siteDefaultLocale.getConfigurableAttributeId(nodeCA);
		const typeClass = siteDefaultLocale.getConfigurableAttributeType(nodeCA);

		const rc = reflection.getClass(typeClass);
		if (!rc) {
			return false;
		}

		const ra = rc.getAttribute(attrId, false, false, true);
		if (!ra) {
			return false;
		}

		return (ra.isPaticipateInAction || ra.hasNotifyLogic);
	},
});

export = MarkForDeletionUOMCustomAttribute;
